<script setup>
import { computed } from 'vue';

const props = defineProps({
  size: {
    type: String,
    default: 'medium',
    validator: (value) => ['small', 'medium', 'large', 'xl'].includes(value)
  },
  color: {
    type: String,
    default: 'mint',
    validator: (value) => ['mint', 'warm', 'gray', 'white'].includes(value)
  },
  fullScreen: {
    type: Boolean,
    default: false
  },
  overlay: {
    type: Boolean,
    default: false
  },
  message: {
    type: String,
    default: ''
  }
});

const sizeClasses = {
  small: 'w-4 h-4',
  medium: 'w-8 h-8',
  large: 'w-12 h-12',
  xl: 'w-16 h-16'
};

const colorClasses = {
  mint: 'text-mint-500',
  warm: 'text-warm-600',
  gray: 'text-gray-500',
  white: 'text-white'
};

const spinnerSize = computed(() => sizeClasses[props.size]);
const spinnerColor = computed(() => colorClasses[props.color]);

const containerClasses = computed(() => {
  const baseClasses = ['flex items-center justify-center'];

  if (props.fullScreen) {
    baseClasses.push('fixed inset-0 z-50');
  }

  if (props.overlay) {
    baseClasses.push('absolute inset-0 bg-white/80 backdrop-blur-sm z-10');
  }

  return baseClasses.join(' ');
});
</script>

<template>
  <div :class="containerClasses">
    <div class="flex flex-col items-center gap-3">
      <!-- Spinner -->
      <div :class="spinnerSize" class="animate-spin">
        <svg
          :class="spinnerColor"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <circle
            class="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            stroke-width="4"
          />
          <path
            class="opacity-75"
            fill="currentColor"
            d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      </div>

      <!-- Loading message -->
      <p v-if="message" :class="spinnerColor" class="text-sm font-medium">
        {{ message }}
      </p>
    </div>
  </div>
</template>

<style scoped>
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.animate-spin {
  animation: spin 1s linear infinite;
}
</style>
